import React from "react";
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import appConfig from "configs/app.config";
import {
  TOKEN_TYPE,
  REQUEST_HEADER_AUTH_KEY,
} from "constants/api.constant";
import { PERSIST_STORE_NAME } from "constants/app.constant";
import deepParseJson from "utils/deepParseJson";
import store from "../store";
import { onSignOutSuccess } from "../store/auth/sessionSlice";
import { setUser } from "../store/auth/userSlice";
import { resetHomeDecisions } from "../utils/homeDecisions";
import { redactRequestSecrets } from "./redactRequestSecrets";
import { Notification, toast } from "components/ui";
import {
  isBackgroundRequest,
  isInBackgroundRequestScope,
} from "./backgroundRequest";
import { markSignedOut, hasSignedOutNotice } from "utils/signedOutNotice";

/**
 * "Signed out" (DW-025). Three things mean it, and all three end the same way: state cleared, one plain line waiting on the
 * sign-in page, no toast, and the route guard sends her to /sign-in keeping the page she was on.
 *  - a 401;
 *  - a 400 whose message is exactly "Unauthorize Request": that is how the API answers a request with NO token
 *    (admin.middleware, MIDDLEWARE_RESPONSE.JWTERROR) until it answers 401 (platform follow-up);
 *  - no token in storage while she still looks signed in (signed out in another tab, storage cleared): the request is not even
 *    sent, so a dashboard of twelve calls is not twelve 400s and a poll does not keep firing.
 * Any OTHER 400 is an ordinary failure and never signs her out.
 */
const SIGNED_OUT_400_TEXT = /^unauthori[sz]e[d]? request\.?$/i;

const isSignedOutAnswer = (response?: { status?: number; data?: { message?: unknown } }): boolean => {
  if (!response || typeof response.status !== "number") return false;
  if (response.status === 401) return true;
  if (response.status !== 400) return false;
  const text = response.data?.message;
  return typeof text === "string" && SIGNED_OUT_400_TEXT.test(text.trim());
};

const looksSignedIn = (): boolean => Boolean(store.getState()?.auth?.session?.signedIn);

// The sign-in call itself has no token, by design.
const isLoginCall = (url?: string): boolean => /\/admin\/login\/?$/.test(url || "");

/** What a request that was never sent looks like to the response interceptor: a 401. */
const signedOutError = (config: InternalAxiosRequestConfig) => {
  const error: any = new Error("Signed out");
  error.code = "ERR_SIGNED_OUT";
  error.config = config;
  error.isAxiosError = true;
  error.response = {
    status: 401,
    statusText: "Unauthorized",
    data: { message: "Signed out", isLogout: true },
    headers: {},
    config,
  };
  return error;
};

const showErrorToast = (error: string) => {
  toast.push(
    <Notification type="danger" closable>
      {error}
    </Notification>
  );
};

let isRefreshingRole = false;

// Has the saved session ever shown a token since she signed in? (See the request interceptor.)
let tokenSeenInStorage = false;

const BaseService = axios.create({
  timeout: 60000,
  baseURL: appConfig.apiBaseUrl,
});

BaseService.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const rawPersistData = localStorage.getItem(PERSIST_STORE_NAME);
    const persistData = deepParseJson(rawPersistData);

    const storedToken = persistData?.auth?.session?.token;
    const signedIn = looksSignedIn();
    if (!signedIn) tokenSeenInStorage = false;
    else if (storedToken) tokenSeenInStorage = true;

    let accessToken = storedToken;
    if (!accessToken && signedIn && !tokenSeenInStorage) {
      // Only just signed in: the saved copy may not be written yet, so use the live one. Once the saved copy has been seen,
      // its absence means the token really is gone.
      accessToken = store.getState()?.auth?.session?.token;
    }
    if (accessToken) {
      config.headers[REQUEST_HEADER_AUTH_KEY] = `${accessToken}`;
    } else if (signedIn && !isLoginCall(config.url)) {
      // She looks signed in but the token is gone: do not send a request the API can only refuse.
      config.adapter = () => Promise.reject(signedOutError(config));
    }

    // A request started by a poll / refetch (see services/backgroundRequest).
    if (isInBackgroundRequestScope()) {
      config.background = true;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
  // Synchronous, so the stamp above sees the scope the request was started in.
  { synchronous: true }
);

/** A button the admin pressed (save, approve, delete) rather than a read. */
const isUserAction = (config?: AxiosRequestConfig): boolean =>
  (config?.method || "get").toLowerCase() !== "get";

BaseService.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ message?: string }>) => {
    // R-562: an axios error carries the whole request, including the admin's
    // `token` header and, on a failed sign-in, the typed password. Scrub them
    // off the error before anything downstream (a console.error in a hook, an
    // error reporter, error.toJSON()) can print them. The request already went
    // out unchanged. Do not log `error` in this file; the toast is how the
    // admin hears about it.
    redactRequestSecrets(error);
    const { response, config } = error;

    // DW-025: signed out. One state change, one notice, no toast; the route guard does the redirect.
    if (isSignedOutAnswer(response)) {
      const wasSignedIn = looksSignedIn();
      store.dispatch(onSignOutSuccess());
      // Step 3: "Just decided" belongs to the admin who pressed; a sign-out clears it.
      resetHomeDecisions();
      if (wasSignedIn) markSignedOut();
      // Quiet when she was signed in (the sign-in page says it), for the token-less 400 and the never-sent request (nothing to
      // say once she is out), and for the rest of the same burst (the notice is already waiting). Only a 401 that arrives when
      // she was never signed in keeps the toast it always had.
      if (wasSignedIn || response?.status === 400 || (error as any)?.code === "ERR_SIGNED_OUT" || hasSignedOutNotice()) {
        return Promise.reject(error);
      }
    }

    // Nobody asked for a background request just now (a poll, a refetch, a
    // sidebar badge): its failure is the page's to show, not a toast's.
    const background = isBackgroundRequest(config);

    if (response?.status === 403) {
      // A forbidden background request — e.g. the Reviews badge polled for a
      // role that cannot open Reviews — is silent: no toast, no role check,
      // and above all no redirect. Redirecting on those sent finance and
      // content roles to Access Denied from every page.
      if (background || isRefreshingRole) {
        return Promise.reject(error);
      }
      isRefreshingRole = true;
      showErrorToast(response?.data?.message || "Permission denied");
      try {
        // The admin's role may have changed since sign-in. With the fresh
        // role, the route guard (AuthorityGuard) sends them to Access Denied
        // if the page they are on is no longer theirs.
        const profileRes = await BaseService.get("/admin/profile", { background: true });
        const freshAdmin = profileRes?.data?.data;
        if (freshAdmin?.role) {
          const currentUser = store.getState().auth.user;
          store.dispatch(setUser({
            ...currentUser,
            role: freshAdmin.role,
            authority: [freshAdmin.role],
          }));
        }
      } catch (e) {
        // profile fetch failed — ignore
      } finally {
        isRefreshingRole = false;
      }
      // A forbidden ACTION still goes to Access Denied. A forbidden READ does
      // not: the dashboard (every role's landing page) reads some
      // super-admin-only data, and a redirect there threw finance and content
      // roles off their own dashboard; the toast says what happened.
      if (isUserAction(config)) {
        window.location.href = "/access-denied";
      }
      return Promise.reject(error);
    }

    if (!background && !config?.handlesOwnError) {
      showErrorToast(response?.data?.message || "Something went wrong");
    }

    return Promise.reject(error);
  }
);

export default BaseService;
